// src/components/groups/GroupPrivateGate.jsx
//
// Minimal private-group gate (Groups Tier 1, §4). Shown to a NON-MEMBER of a PRIVATE group
// when there is no published community_landing to route to. It is deliberately lean: group
// name + "private" + a join CTA, and NOTHING else — no members, no sub-groups, no feed, no
// interior fetch at all (privacy by construction; a private group does not exist to outsiders).
//
// Join CTA: logged-out → "Log in to Join" (opens login). Logged-in → "Join" for auto-accept
// groups, or "Request to join" (opens the optional-message modal) for approval groups.
// On a successful join/request the parent re-resolves the gate (onMembershipChange).
//
// Three group types (addendum 5): the name and description come from get_group_introduction,
// which answers for a findable private group (its row is for members only). An invitation-only
// group gives no answer to an outsider — nor does a missing or deleted one, by design — so the
// gate then shows one neutral line and no CTA: there is no request route into it (R1).

import React, { useState, useEffect } from 'react';
import { ArrowLeft } from 'lucide-react';
import { useTheme } from '../../themes/ThemeProvider';
import { useTranslation } from '../../i18n/useTranslation';
import { useAuth } from '../../contexts/AuthContext';
import { groupService } from '../../data/dataService';
import { supabaseFetch } from '../../lib/supabase';
import JoinRequestModal from './JoinRequestModal';
import { rateLimitMessage } from '../../utils/rateLimitMessage';

const GroupPrivateGate = ({ group, currentUser, isLoggedIn = false, onShowLoginModal, onMembershipChange, onBack }) => {
  const theme = useTheme();
  const { t } = useTranslation('groups');
  const { requireAuth } = useAuth();
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [loading, setLoading] = useState(false);
  const [joinMessage, setJoinMessage] = useState('');

  const mauve = theme?.primary || '#BB4D9A';
  // undefined = still asking; { found, ... } once answered (see the header).
  const [intro, setIntro] = useState(undefined);
  useEffect(() => {
    let cancelled = false;
    setIntro(undefined);
    if (!group?.id) return undefined;
    groupService.getGroupIntroduction(group.id).then(r => { if (!cancelled) setIntro(r); });
    return () => { cancelled = true; };
  }, [group?.id]);
  // Both private types never admit anyone directly; only a failed read falls back to the prop.
  const autoAccept = intro?.found ? false : group?.auto_accept_members !== false;
  const canAskToJoin = !intro || intro.error || intro.found;
  const displayName = (intro?.found && intro.name) || group?.name;
  const displayDescription = intro?.found ? intro.description : group?.description;

  // group (selectedGroup) may carry whatever narrow `select` list its source card query used
  // — is_federation is a non-null boolean column, so `undefined` here can only mean a `select`
  // omitted it. Mirrors useGroupHome.js's completeGroup upgrade-fetch (3b1caa7e): never gate
  // the join CTA on a partial group object.
  const [isFederation, setIsFederation] = useState(group?.is_federation);
  useEffect(() => {
    setIsFederation(group?.is_federation);
    if (!group?.id || group.is_federation !== undefined) return;
    let cancelled = false;
    supabaseFetch.query('communities', {
      select: 'is_federation',
      eq: { id: group.id },
      single: true
    }).then(({ data, error }) => {
      if (cancelled || error || !data) return;
      setIsFederation(data.is_federation);
    });
    return () => { cancelled = true; };
  }, [group?.id, group?.is_federation]);

  if (!group) return null;

  const doJoin = async (message) => {
    setLoading(true);
    try {
      const result = await groupService.joinGroup(currentUser.id, group.id, message);
      if (result?.success && onMembershipChange) {
        onMembershipChange();
      } else if (result?.status === 'blocked') {
        setJoinMessage(t('cannot_request_to_join'));
      } else if (result?.status === 'rate_limited') {
        setJoinMessage(rateLimitMessage(result.error, t) || t('common:rate_limit_generic'));
      }
    } catch (e) {
      console.error('Private gate join error:', e);
    } finally {
      setLoading(false);
      setShowJoinModal(false);
    }
  };

  const handleJoinClick = () => {
    // Logged-out: send to login (the explicit "Log in to Join" affordance)
    if (!isLoggedIn || !currentUser?.id) {
      onShowLoginModal?.();
      return;
    }
    requireAuth('gate_body_join_group', () => {
      if (autoAccept) doJoin(null);
      else setShowJoinModal(true);
    });
  };

  const buttonLabel = !isLoggedIn
    ? t('login_to_join')
    : (autoAccept ? t('ui:join') : t('request_to_join'));

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '60px 20px',
      textAlign: 'center',
      maxWidth: '500px',
      margin: '0 auto',
      position: 'relative'
    }}>
      {/* Back — returns to the previous group's Home tab (or to group discovery). Without this a
          private sub-group reached from a parent is a dead end. */}
      {onBack && (
        <button
          onClick={onBack}
          style={{
            position: 'absolute', top: '12px', left: '12px',
            display: 'flex', alignItems: 'center', gap: '5px',
            background: 'none', border: 'none', cursor: 'pointer', padding: '4px',
            color: mauve, fontWeight: 600, fontSize: '14px',
            fontFamily: 'OpenSauceSans, sans-serif'
          }}
        >
          <ArrowLeft size={18} strokeWidth={2.5} />
          {t('common:back')}
        </button>
      )}

      {/* Private badge — lock, no interior */}
      <div style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '6px',
        padding: '8px 20px',
        background: '#6b7280',
        color: 'white',
        borderRadius: '20px',
        fontSize: '14px',
        fontWeight: '600',
        marginBottom: '24px'
      }}>
        🔒 {t('ui:private_group')}
      </div>

      {/* Group name — public identity only */}
      {displayName && (
        <h2 style={{ fontSize: '24px', fontWeight: 'bold', color: '#1f2937', marginBottom: '12px' }}>
          {displayName}
        </h2>
      )}

      {/* Public blurb (the group's own description — not interior content) */}
      {displayDescription && (
        <p style={{ fontSize: '15px', color: '#6b7280', lineHeight: '1.6', marginBottom: '20px' }}>
          {displayDescription}
        </p>
      )}

      <p style={{ fontSize: '14px', color: '#9ca3af', marginBottom: '24px' }}>
        {canAskToJoin ? t('ui:private_group_message') : t('gate_cannot_show_or_invitation_only')}
      </p>

      {/* Join CTA — never offered on a federation (4C §5: federates groups, not people), nor on
          a group that can't be shown (invitation only, R1). Hidden until the type is known. */}
      {!isFederation && intro !== undefined && canAskToJoin && (
        <button
          onClick={handleJoinClick}
          disabled={loading}
          style={{
            padding: '12px 32px',
            background: mauve,
            color: 'white',
            border: 'none',
            borderRadius: '25px',
            fontSize: '16px',
            fontWeight: '600',
            cursor: loading ? 'wait' : 'pointer',
            opacity: loading ? 0.7 : 1,
            transition: 'opacity 0.2s'
          }}
          onMouseOver={(e) => { if (!loading) e.currentTarget.style.opacity = '0.9'; }}
          onMouseOut={(e) => { if (!loading) e.currentTarget.style.opacity = '1'; }}
        >
          {buttonLabel}
        </button>
      )}

      {joinMessage && (
        <p style={{ fontSize: '13px', color: '#9ca3af', marginTop: '12px' }}>
          {joinMessage}
        </p>
      )}

      {/* Approval groups: optional-message modal */}
      {!isFederation && showJoinModal && (
        <JoinRequestModal
          isOpen={showJoinModal}
          onClose={() => setShowJoinModal(false)}
          onSubmit={doJoin}
          groupName={displayName}
          loading={loading}
        />
      )}
    </div>
  );
};

export default GroupPrivateGate;
